import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { StytchB2B } from '@stytch/react/b2b';
import { useEffect, useMemo, useState } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { buildB2BDiscoveryConfig } from '@/auth/b2bDiscovery';
import { useAuth } from '../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  goToNextOrHome,
  needsDocumentLoad,
  validateNextSearch,
} from '@/lib/nextPath';
import { BootScreen } from '@/components/BootScreen';
import { AuthBrandLockup } from '@/components/AuthBrandLockup';
import * as chatPrefs from '@/utils/chatPrefs';
import { useStore } from '@/stores/useStore';
import { DEFAULT_CHAT_APPROVAL_MODE } from '@/stores/chatApprovalMode';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import tosContent from '@/content/terms-of-service.md?raw';
import privacyContent from '@/content/privacy-policy.md?raw';

type OpenDialog = 'tos' | 'privacy' | null;

function LoginRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { next } = Route.useSearch();
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const b2bConfig = useMemo(
    () => buildB2BDiscoveryConfig(window.location.origin, next),
    [next],
  );

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page: 'login',
    });
  }, [posthog]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void goToNextOrHome(navigate, next);
    }
  }, [isAuthenticated, isLoading, navigate, next]);

  // Single session-end choke point: landing on /login (unauthenticated) means a
  // session ended — wipe persisted chat state so the next user on a shared
  // browser never inherits the prior user's open panel or session. All real-auth
  // redirect-to-login paths (logout, expiry, the _app auth guard) funnel here.
  // Reset the LIVE store too, not just localStorage: `chatOpen` is seeded from
  // the persisted key once at boot, and a no-reload SPA login (next user, no
  // `?next`) reuses that store instance — clearing only localStorage would leave
  // the in-memory `chatOpen=true` and re-open the panel for the next user.
  //
  // Defense in depth on the `isAuthenticated` check: `beforeLoad` bounces an
  // authenticated visitor before this ever mounts, and the render below returns
  // early — but neither protects a mount effect, and this one destroys state.
  useEffect(() => {
    if (isAuthenticated) return;
    for (const prefix of [
      'hand-calc',
      'hand-calc-session',
      'hand-calc-draft',
    ]) {
      queryClient.removeQueries({ queryKey: [prefix] });
    }
    useStore.getState().setChatOpen(false);
    useStore.getState().setChatApprovalMode(DEFAULT_CHAT_APPROVAL_MODE);
    chatPrefs.clearOnLogout();
    // Mount-only: this marks the session boundary, not every auth transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign-in succeeded and the effect above is navigating away. React paints
  // before effects run, so without this the completed login re-paints its own
  // form for a frame — and the browser has autofilled it by then, which reads
  // as "the login screen came back". Show the boot screen the rest of the
  // bootstrap is already using so the hand-off is invisible.
  if (isAuthenticated) return <BootScreen />;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/6 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-info/6 blur-3xl" />
        <div className="absolute top-1/3 right-0 h-72 w-72 rounded-full bg-muted opacity-70 blur-3xl" />
      </div>

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center p-4">
        <div className="mb-8 text-center">
          <AuthBrandLockup />
          <p className="text-muted-foreground">{t('auth.login.tagline')}</p>
        </div>

        <div className="w-full max-w-md">
          <div className="flex justify-center">
            <StytchB2B config={b2bConfig} />
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            {t('auth.login.agreePrefix')}{' '}
            <button
              type="button"
              className="underline underline-offset-4 transition-colors hover:text-foreground"
              onClick={() => setOpenDialog('tos')}
            >
              {t('auth.login.termsOfService')}
            </button>{' '}
            {t('auth.login.and')}{' '}
            <button
              type="button"
              className="underline underline-offset-4 transition-colors hover:text-foreground"
              onClick={() => setOpenDialog('privacy')}
            >
              {t('auth.login.privacyPolicy')}
            </button>
            .
          </p>
        </div>
      </div>

      <Dialog
        open={openDialog !== null}
        onOpenChange={(open) => {
          if (!open) setOpenDialog(null);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {openDialog === 'tos'
                ? t('auth.login.termsOfService')
                : t('auth.login.privacyPolicy')}
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm prose-gray max-h-[60vh] max-w-none overflow-y-auto [&_h1]:mb-4 [&_h1]:text-lg [&_h1]:font-bold [&_h2]:mt-6 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_li]:mb-1 [&_p]:mb-3 [&_p]:text-sm [&_p]:text-muted-foreground [&_strong]:text-foreground [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:text-sm [&_ul]:text-muted-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {openDialog === 'tos' ? tosContent : privacyContent}
            </ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>

      <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()}{' '}
        <a
          href="https://www.khorium.ai/"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-colors hover:text-foreground"
        >
          Khorium Inc.
        </a>{' '}
        {t('auth.login.copyright')}
      </p>
    </div>
  );
}

export const Route = createFileRoute('/login')({
  validateSearch: validateNextSearch,
  beforeLoad: ({ context, search }) => {
    if (!context.auth.isLoading && context.auth.isAuthenticated) {
      if (search.next)
        throw redirect({
          href: search.next,
          reloadDocument: needsDocumentLoad(search.next),
        });
      throw redirect({ to: '/' });
    }
  },
  component: LoginRoute,
});
